import MediaLibraryClient from '../clients/MediaLibraryClient';
import MediaLibraryEvents from './events';
import { displayErrorFeedbackMessage, displayInfoFeedbackMessage } from '../utilities';

export default class MediaLibrarySearchSelection
{
    constructor()
    {
        // Imports
        this.mediaLibraryClient = new MediaLibraryClient();

        // Elements
        this.selectAllButton = document.querySelector('.js-media-library__button--select-all');
        this.deselectAllButton = document.querySelector('.js-media-library__button--deselect-all');
        this.selectDownloadFormats = document.querySelectorAll('.js-media-library__checkbox--download-format');
        this.selectDownloadFormatVideo = document.querySelector('.js-media-library__checkbox--download-format--video');
        this.selectDownloadFormatThreeSixty =
            document.querySelector('.js-media-library__checkbox--download-format--threesixty');
        this.selectDownloadFormatPng = document.querySelector('.js-media-library__checkbox--download-format--png');
        this.selectDownloadFormatJpg = document.querySelector('.js-media-library__checkbox--download-format--jpg');
        this.selectDownloadFormatPsd = document.querySelector('.js-media-library__checkbox--download-format--psd');
        this.selectDownloadFormatSvg = document.querySelector('.js-media-library__checkbox--download-format--svg');
        this.selectDownloadFormatEps = document.querySelector('.js-media-library__checkbox--download-format--eps');

        // Selectors
        this.tileSelector = ('.js-media-library__tile');
        this.checkboxSelector = ('.js-media-library__checkbox--choose');

        // State Classes
        this.isVideoClass = ('js-media-library__tile--is-video');
        this.isThreeSixtyClass = ('js-media-library__tile--is-threesixty');
        this.isImageClass = ('js-media-library__tile--is-image');
        this.isLogoClass = ('js-media-library__tile--is-logo');

        // Events
        this.assetSelectedEvent = new Event(MediaLibraryEvents.assetSelectedEvent);
        this.noAssetSelectedEvent = new Event(MediaLibraryEvents.noAssetSelectedEvent);

        // Feedback
        this.singleSelectionLimitReachedFeedback = document
            .querySelector('meta[name="feedback.media.library.single.selection.limit.reached"]')
            .getAttribute('content');
        this.allSelectionLimitReachedFeedback = document
            .querySelector('meta[name="feedback.media.library.all.selection.limit.reached"]')
            .getAttribute('content');

        // Helpers
        this.selectedAssets = [];
        this.currentPage = null;
        this.queryData = null;
        this.totalAssets = null;
        this.selectionLimit = 1000;
    }

    initEvents()
    {
        this.selectAllButton.addEventListener('click', () =>
        {
            this.selectAllAssets();
        });

        this.deselectAllButton.addEventListener('click', () =>
        {
            this.deselectAllAssets();
        });
    }

    initTiles()
    {
        let tiles = document.querySelectorAll(this.tileSelector);

        tiles.forEach(tile =>
        {
            let { assetId } = tile.querySelector(this.checkboxSelector).dataset;

            if (this.selectedAssets.find((asset) => asset.assetId === assetId))
            {
                tile.classList.add('selected');
                tile.querySelector(this.checkboxSelector).checked = true;
            }

            tile.querySelector(this.checkboxSelector).addEventListener('click', event =>
            {
                this.selectSingleAsset(event, tile);
            });
        });
    }

    /**
	 * @returns {Array} selected asset ids
	 */
    getSelectedAssets()
    {
        return this.selectedAssets;
    }

    /**
	 * @param {object[]} assets assets
	 */
    setSelectedAssets(assets)
    {
        this.selectedAssets = assets;
    }

    resetSelectedAssets()
    {
        this.selectedAssets = [];
    }

    /**
	 * @param {String} currentPage current page
	 * @param {Object} queryData query data
	 * @param {number} totalAssets total assets
	 */
    setSelectAllData(currentPage, queryData, totalAssets)
    {
        this.currentPage = currentPage;
        this.queryData = queryData;
        this.totalAssets = totalAssets;
    }

    /**
	 * @param {Object} event event object
	 * @param {HTMLElement} tile tile to select
	 */
    selectSingleAsset(event, tile)
    {
        let selectedCheckbox = tile.querySelector(this.checkboxSelector);
        let selectedAssetId = selectedCheckbox.dataset.assetId;

        if (selectedCheckbox.checked && this.selectedAssets.length < this.selectionLimit)
        {
            this.selectedAssets.push({
                assetId: selectedAssetId,
                videoFilePath: selectedCheckbox.dataset.assetVideoFilePath,
                archiveFilePath: selectedCheckbox.dataset.assetArchiveFilePath,
                psdFilePath: selectedCheckbox.dataset.assetPsdFilePath,
                jpgFilePath: selectedCheckbox.dataset.assetJpgFilePath,
                pngFilePath: selectedCheckbox.dataset.assetPngFilePath,
                svgFilePath: selectedCheckbox.dataset.assetSvgFilePath,
                epsFilePath: selectedCheckbox.dataset.assetEpsFilePath

            });

            tile.classList.add('selected');

            let formatMap = {
                [this.isVideoClass]: this.selectDownloadFormatVideo,
                [this.isThreeSixtyClass]: this.selectDownloadFormatThreeSixty,
                [this.isImageClass]: this.selectDownloadFormatJpg,
                [this.isLogoClass]: this.selectDownloadFormatSvg
            };

            for (let [className, checkbox] of Object.entries(formatMap))
            {
                if (
                    tile.classList.contains(className) &&
                    (className !== this.isImageClass ||
                    (!this.selectDownloadFormatPng.checked &&
                    !this.selectDownloadFormatPsd.checked)) &&
                    (className !== this.isLogoClass ||
                    !this.selectDownloadFormatEps.checked)
                )
                {
                    checkbox.checked = true;
                    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                    break;
                }
            }
        }
        else if (selectedCheckbox.checked && this.selectedAssets.length === this.selectionLimit)
        {
            event.preventDefault();

            displayInfoFeedbackMessage(this.singleSelectionLimitReachedFeedback);
        }
        else
        {
            this.selectedAssets = this.selectedAssets.filter(asset =>
            {
                return asset.assetId !== selectedAssetId;
            });

            tile.classList.remove('selected');
        }

        if (this.selectedAssets.length)
        {
            document.dispatchEvent(this.assetSelectedEvent);
            this.deselectAllButton.classList.remove('disabled');

            if (!this.selectedAssets.some(asset => asset.jpgFilePath))
            {
                this.selectDownloadFormatPng.checked = false;
                this.selectDownloadFormatJpg.checked = false;
                this.selectDownloadFormatPsd.checked = false;

                this.selectDownloadFormatJpg.dispatchEvent(new Event('change', { bubbles: true }));
            }

            if (!this.selectedAssets.some(asset => asset.svgFilePath))
            {
                this.selectDownloadFormatSvg.checked = false;
                this.selectDownloadFormatEps.checked = false;

                this.selectDownloadFormatSvg.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
        else
        {
            this.selectDownloadFormats.forEach(format =>
            {
                format.checked = false;
                this.selectDownloadFormatJpg.dispatchEvent(new Event('change', { bubbles: true }));
            });

            document.dispatchEvent(this.noAssetSelectedEvent);
            this.deselectAllButton.classList.add('disabled');
        }
    }

    selectAllAssets()
    {
        if (this.totalAssets <= this.selectionLimit)
        {
            this.fetchAllAssetsForSelection().then(json =>
            {
                const assets = this.processAllAssetsForSelection(json);

                this.selectedAssets.push(...assets);

                const tiles = document.querySelectorAll(this.tileSelector + ':not(.hidden)');

                tiles.forEach(tile =>
                {
                    let selectedCheckbox = tile.querySelector(this.checkboxSelector);

                    tile.classList.add('selected');
                    selectedCheckbox.checked = true;
                });

                this.deselectAllButton.classList.remove('disabled');

                document.dispatchEvent(this.assetSelectedEvent);
            });
        }
        else
        {
            displayErrorFeedbackMessage(this.allSelectionLimitReachedFeedback);
        }
    }

    /**
     * @returns {Promise<Response>}
     */
    fetchAllAssetsForSelection()
    {
        if (this.currentPage === 'search')
        {
            if (this.queryData.eanBatch || this.queryData.productNumberBatch)
            {
                return this.mediaLibraryClient.fetchAssetsByBatchSearch(
                    this.queryData.eanBatch,
                    this.queryData.productNumberBatch,
                    this.queryData.newFilterSet,
                    this.queryData.videoFilterSet,
                    this.queryData.threeSixtyFilterSet,
                    this.queryData.logoFilterSet,
                    this.totalAssets,
                    0
                )
                    .then(response => response.json());
            }
            else
            {
                return this.mediaLibraryClient.fetchAssetsBySearchTerm(
                    this.queryData.searchField,
                    this.queryData.searchValue,
                    this.queryData.newFilterSet,
                    this.queryData.videoFilterSet,
                    this.queryData.threeSixtyFilterSet,
                    this.queryData.logoFilterSet,
                    this.totalAssets,
                    0
                )
                    .then(response => response.json());
            }
        }
        else if (this.currentPage === 'collection')
        {
            return this.mediaLibraryClient.fetchAssetsByAssetIds(
                Object.values(this.queryData),
                this.totalAssets,
                0
            )
                .then(response => response.json());
        }
    }

    /**
	 * @param {Object} json all assets
     * @returns {Array} processed assets
	 */
    processAllAssetsForSelection(json)
    {
        const formatMap = {
            videoFilePath: this.selectDownloadFormatVideo,
            archiveFilePath: this.selectDownloadFormatThreeSixty,
            jpgFilePath: this.selectDownloadFormatJpg,
            svgFilePath: this.selectDownloadFormatSvg
        };

        let assets = [];

        json.items.forEach(asset =>
        {
            if (!this.selectedAssets.find((selectedAsset) => selectedAsset.assetId === asset.attributes.assetId))
            {
                let selectedAsset = {
                    assetId: asset.attributes.assetId,
                    videoFilePath:
                        asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.mp4')
                            ? asset.assetFiles['none'].path + '/' + asset.assetFiles['none'].filename : '',
                    archiveFilePath:
                        asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.zip')
                            ? asset.assetFiles['none'].path + '/' + asset.assetFiles['none'].filename : '',
                    psdFilePath:
                        asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.psd')
                            ? asset.assetFiles['none'].path + '/' + asset.assetFiles['none'].filename : '',
                    jpgFilePath:
                        asset.assetFiles['jpeg-max'] && asset.assetFiles['jpeg-max'].filename.includes('.jpeg')
                            ? asset.assetFiles['jpeg-max'].path + '/' + asset.assetFiles['jpeg-max'].filename : '',
                    pngFilePath:
                        asset.assetFiles['png-max'] && asset.assetFiles['png-max'].filename.includes('.png')
                            ? asset.assetFiles['png-max'].path + '/' + asset.assetFiles['png-max'].filename : '',
                    svgFilePath:
                        asset.assetFiles['none'] && asset.assetFiles['none'].filename.includes('.svg')
                            ? asset.assetFiles['none'].path + '/' + asset.assetFiles['none'].filename : '',
                    epsFilePath:
                        asset.assetFiles['vector-eps'] && asset.assetFiles['vector-eps'].filename.includes('.eps')
                            ? asset.assetFiles['vector-eps'].path + '/' + asset.assetFiles['vector-eps'].filename : ''
                };

                assets.push(selectedAsset);

                for (let [key, checkbox] of Object.entries(formatMap))
                {
                    if (
                        selectedAsset[key]  &&
                        (key !== 'jpgFilePath' ||
                        (!this.selectDownloadFormatPng.checked
                        && !this.selectDownloadFormatPsd.checked)) &&
                        (key !== 'svgFilePath' ||
                        !this.selectDownloadFormatEps.checked)
                    )
                    {
                        checkbox.checked = true;
                        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                        break;
                    }
                }
            }
        });

        return assets;
    }

    deselectAllAssets()
    {
        document.dispatchEvent(this.noAssetSelectedEvent);
        this.deselectAllButton.classList.add('disabled');

        let tiles = document.querySelectorAll(this.tileSelector);

        tiles.forEach(tile =>
        {
            tile.classList.remove('selected');
            tile.querySelector(this.checkboxSelector).checked = false;
        });

        this.selectedAssets = [];

        this.selectDownloadFormats.forEach(format =>
        {
            format.checked = false;
            format.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }
}
